import { useState, useEffect } from 'react';
import { useNotifications } from '../../../../context/NotificationContext';
import { useAuth } from '../../../../context/AuthContext';

interface UseMarkerNotificationsProps {
  isOpen: boolean;
}

export const useMarkerNotifications = ({ isOpen: _isOpen }: UseMarkerNotificationsProps) => {
  const { state: notificationState, markAsRead } = useNotifications();
  const { supabaseUser } = useAuth();

  // État pour suivre les alertes lues
  const [readAlerts, setReadAlerts] = useState<{ [key: string]: boolean }>(() => {
    // Récupérer l'état depuis localStorage au chargement
    const savedReadAlerts = localStorage.getItem('readAlerts');
    return savedReadAlerts ? JSON.parse(savedReadAlerts) : {};
  });

  // Synchroniser l'état des alertes avec le système de notifications
  useEffect(() => {
    // Créer un nouvel état basé sur les notifications du système
    const newReadAlerts: { [key: string]: boolean } = {};
    
    // Pour chaque notification liée à une action
    notificationState.notifications.forEach(notification => {
      if (notification.actionId) {
        // Si la notification est marquée comme lue dans le système
        if (notification.status === 'read') {
          newReadAlerts[notification.actionId] = true;
        }
      }
    });
    
    // Mettre à jour l'état local et le localStorage
    setReadAlerts(newReadAlerts);
    localStorage.setItem('readAlerts', JSON.stringify(newReadAlerts));
  }, [notificationState.notifications]);

  // Fonction pour marquer une alerte comme lue ou non lue
  const toggleAlertRead = (actionId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Empêcher la propagation vers le parent
    
    // Trouver la notification correspondante à cette action
    const relatedNotification = notificationState.notifications.find(
      notification => notification.actionId === actionId
    );
    
    if (!relatedNotification) {
      console.warn(`Aucune notification trouvée pour l'action ${actionId}`);
      return;
    }
    
    // Vérifier si l'utilisateur connecté est le propriétaire de la notification
    if (supabaseUser && relatedNotification.user_id !== supabaseUser.id) {
      console.warn(`L'utilisateur ${supabaseUser.id} ne peut pas modifier la notification ${relatedNotification.id} appartenant à ${relatedNotification.user_id}`);
      return; // Empêcher la modification si ce n'est pas la notification de l'utilisateur
    }
    
    // Marquer comme lu dans le système de notifications
    markAsRead(relatedNotification.id);
    console.log(`Notification ${relatedNotification.id} marquée comme lue via le système de notifications`);
    
    // Mettre à jour l'état local pour l'affichage visuel
    const newReadAlerts = {
      ...readAlerts,
      [actionId]: !readAlerts[actionId]
    };
    
    setReadAlerts(newReadAlerts);
    localStorage.setItem('readAlerts', JSON.stringify(newReadAlerts));
    
    console.log(`Alerte ${actionId} marquée comme ${readAlerts[actionId] ? 'non lue' : 'lue'}`);
  };

  // Fonction pour vérifier si une notification peut être modifiée par l'utilisateur connecté
  const canToggleNotification = (actionId: string): boolean => {
    const relatedNotification = notificationState.notifications.find(
      notification => notification.actionId === actionId
    );
    
    if (!relatedNotification || !supabaseUser) {
      return false;
    }
    
    // L'utilisateur peut modifier uniquement ses propres notifications
    return relatedNotification.user_id === supabaseUser.id;
  };

  // Ancien système de tooltip supprimé - remplacé par le système unifié dans chaque composant

  return {
    readAlerts,
    toggleAlertRead,
    canToggleNotification
  };
};
